import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Search, Building2, Trash2, Pencil, Copy, Send, Link as LinkIcon, CheckCircle2 } from "lucide-react";
import { formatDate } from "@/lib/format";

export default function ClientsListPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [inviteInfo, setInviteInfo] = useState(null); // { name, email, invite_url }
  const [inviteCopied, setInviteCopied] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients", { params: q ? { q } : {} });
      setClients(r.data);
    } catch (e) { toast.error("Erro ao carregar clientes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load();  }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t);  }, [q]);

  const openNew = () => { setEditing(null); setForm({ status: "ativo" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const r = await api.patch(`/clients/${editing.id}`, form);
        toast.success("Cliente atualizado");
        setClients((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
      } else {
        const r = await api.post("/clients", form);
        toast.success("Cliente criado. Convite gerado.");
        setClients((prev) => [r.data, ...prev]);
        setInviteInfo({ name: r.data.name, email: r.data.email, invite_url: r.data.invite_url });
        setInviteCopied(false);
      }
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const copyInvite = async () => {
    if (!inviteInfo?.invite_url) return;
    try {
      await navigator.clipboard.writeText(inviteInfo.invite_url);
      setInviteCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setInviteCopied(false), 2500);
    } catch { toast.error("Falha ao copiar"); }
  };

  const resendInvite = async (client) => {
    try {
      const r = await api.post(`/clients/${client.id}/resend-invite`);
      setInviteInfo({ name: client.name, email: client.email, invite_url: r.data.invite_url });
      setInviteCopied(false);
      toast.success("Convite reenviado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este cliente?")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Cliente removido"); setClients((p) => p.filter((c) => c.id !== id)); }
    catch { toast.error("Erro ao remover"); }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Cadastro</p>
          <h1 className="font-heading text-3xl font-bold mt-1">Clientes</h1>
        </div>
        <Button onClick={openNew} data-testid="new-client-btn" className="gap-2">
          <Plus size={16} /> Novo cliente
        </Button>
      </header>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, empresa, CPF/CNPJ, e-mail…" className="pl-9" data-testid="clients-search-input" />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 rounded" />)}</div>
          ) : clients.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              <Building2 className="mx-auto mb-3 text-muted-foreground/50" />
              Nenhum cliente cadastrado ainda.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome / Empresa</TableHead>
                  <TableHead>CPF/CNPJ</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-center">Projetos</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((c) => (
                  <TableRow key={c.id} className="hover:bg-muted/40 cursor-pointer" data-testid={`client-row-${c.id}`}>
                    <TableCell onClick={() => navigate(`/clients/${c.id}`)}>
                      <p className="font-medium">{c.name}</p>
                      {c.company && <p className="text-xs text-muted-foreground">{c.company}</p>}
                    </TableCell>
                    <TableCell className="font-mono text-xs" onClick={() => navigate(`/clients/${c.id}`)}>{c.document || "-"}</TableCell>
                    <TableCell onClick={() => navigate(`/clients/${c.id}`)}>
                      <p className="text-sm">{c.email}</p>
                      <p className="text-xs text-muted-foreground">{c.phone || c.whatsapp || "-"}</p>
                    </TableCell>
                    <TableCell>
                      <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 border ${c.status === "ativo" ? "text-emerald-600 border-emerald-500/30 bg-emerald-500/10" : "text-muted-foreground border-border bg-muted"}`}>
                        {c.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(c.created_at)}</TableCell>
                    <TableCell className="text-center font-medium">{c.project_count}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); resendInvite(c); }} title="Enviar convite de acesso" data-testid={`resend-invite-${c.id}`}>
                        <Send size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(c); }} data-testid={`edit-client-${c.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="text-brand-red hover:text-brand-red" data-testid={`delete-client-${c.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle data-testid="client-form-title">{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome" required>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-form-name" />
            </Field>
            <Field label="Empresa">
              <Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="client-form-company" />
            </Field>
            <Field label="CPF/CNPJ">
              <Input value={form.document || ""} onChange={(e) => setForm({ ...form, document: e.target.value })} data-testid="client-form-document" />
            </Field>
            <Field label="E-mail" required>
              <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="client-form-email" />
            </Field>
            <Field label="Telefone">
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-form-phone" />
            </Field>
            <Field label="WhatsApp">
              <Input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="client-form-whatsapp" />
            </Field>
            <Field label="Endereço" full>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-form-address" />
            </Field>
            <Field label="Responsável">
              <Input value={form.responsible || ""} onChange={(e) => setForm({ ...form, responsible: e.target.value })} data-testid="client-form-responsible" />
            </Field>
            <Field label="Status">
              <select value={form.status || "ativo"} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="client-form-status">
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </Field>
            <Field label="Observações" full>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="client-form-notes" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="client-form-cancel">Cancelar</Button>
            <Button onClick={save} disabled={!form.name || !form.email} data-testid="client-form-save">
              {editing ? "Salvar" : "Cadastrar e gerar convite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite generated modal */}
      <Dialog open={!!inviteInfo} onOpenChange={(o) => !o && setInviteInfo(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon size={18} className="text-primary" /> Convite de acesso gerado
            </DialogTitle>
          </DialogHeader>
          {inviteInfo && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                O link foi gerado para a conta <b className="text-foreground">{inviteInfo.email}</b>. Para enviar o e-mail de acesso, clique no botão "Enviar convite de acesso" na lista, ou copie o link abaixo para enviar manualmente para
                <b className="text-foreground"> {inviteInfo.name}</b> criar sua senha de acesso ao Portal BAUEN.
              </p>
              <div className="rounded-lg border bg-muted/40 p-3">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Link de convite (válido por 7 dias)</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs break-all font-mono text-foreground" data-testid="invite-url-text">{inviteInfo.invite_url}</code>
                  <Button size="sm" variant="outline" onClick={copyInvite} className="shrink-0 gap-1.5" data-testid="copy-invite-btn">
                    {inviteCopied ? <><CheckCircle2 size={14} className="text-emerald-500" /> Copiado</> : <><Copy size={14} /> Copiar</>}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Se o cliente não receber o e-mail, você pode compartilhar este link diretamente por WhatsApp ou outro canal seguro.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setInviteInfo(null)} data-testid="invite-close-btn">Concluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="mb-1.5 block text-xs font-medium">{label} {required && <span className="text-brand-red">*</span>}</Label>
      {children}
    </div>
  );
}
